import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";
import { getVerticalTracker, patchVerticalTracker } from "@/lib/chiroApi";

const TEXT_FIELDS = [
    { key: "name", label: "Name", input: true },
    { key: "business_type", label: "Business type", input: true },
    { key: "default_search_query", label: "Default search query", input: true },
    { key: "default_proof_search_template", label: "Proof Google-Search template", input: true },
    { key: "default_proof_maps_template", label: "Proof Google-Maps template", input: true },
    { key: "default_audit_url_template", label: "Default audit URL template", input: true },
    { key: "default_cta", label: "Default CTA", rows: 2 },
    { key: "summary", label: "Summary", rows: 3 },
    { key: "notes", label: "Notes", rows: 3 },
];

// iter23 — per-tracker scoring config. Editing these adjusts how every
// prospect under campaigns linked to this tracker is scored. Empty values
// fall back to the global defaults (no_website=50, social=30, low_review=25,
// low_rating=20, low_review_count_threshold=25, low_rating_threshold=4.0,
// low_rating_min_review_count=25).
const SCORING_NUMBER_FIELDS = [
    { key: "low_review_count_threshold", label: "Low review-count threshold", help: "Reviews below this trigger the low-review-count opportunity bonus.", step: 1 },
    { key: "low_rating_threshold", label: "Low rating threshold", help: "Ratings below this trigger the low-rating opportunity bonus (only when sample is large enough).", step: 0.1 },
    { key: "low_rating_min_review_count", label: "Low-rating min reviews", help: "Minimum review count before a low rating is statistically meaningful.", step: 1 },
    { key: "no_website_opportunity_points", label: "No website — points", help: "Opportunity points added when prospect has no website.", step: 1 },
    { key: "social_profile_opportunity_points", label: "Social-only website — points", help: "Opportunity points when website is a social/profile/builder URL only.", step: 1 },
    { key: "low_review_opportunity_points", label: "Low review-count — points", help: "Opportunity points when review count is below threshold.", step: 1 },
    { key: "low_rating_opportunity_points", label: "Low rating — points", help: "Opportunity points when rating is below threshold (with sufficient sample).", step: 1 },
];

export default function VerticalTrackerDetail() {
    const { id } = useParams();
    const [t, setT] = useState(null);
    const [saving, setSaving] = useState(false);
    const [disqualifiersText, setDisqualifiersText] = useState("");
    const [domainsText, setDomainsText] = useState("");

    useEffect(() => {
        getVerticalTracker(id).then((d) => {
            setT(d);
            setDisqualifiersText((d.default_disqualifiers || []).join("\n"));
            setDomainsText((d.social_profile_domains || []).join("\n"));
        });
    }, [id]);

    if (!t) {
        return (
            <div className="font-mono text-[12px] text-[#9ca3af]" data-testid="tracker-loading">
                Loading…
            </div>
        );
    }

    const update = (k, v) => setT((cur) => ({ ...cur, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            const body = {};
            for (const f of TEXT_FIELDS) body[f.key] = t[f.key] ?? null;
            body.default_disqualifiers = disqualifiersText
                .split("\n")
                .map((s) => s.trim())
                .filter(Boolean);
            // iter23 — scoring config: send empty strings as null so the
            // backend falls back to the global default.
            for (const f of SCORING_NUMBER_FIELDS) {
                const raw = t[f.key];
                if (raw === "" || raw === null || raw === undefined) {
                    body[f.key] = null;
                } else {
                    const n = Number(raw);
                    body[f.key] = Number.isFinite(n) ? n : null;
                }
            }
            body.social_profile_domains = domainsText
                .split("\n")
                .map((s) => s.trim().toLowerCase())
                .filter(Boolean);
            const next = await patchVerticalTracker(id, body);
            setT(next);
            setDomainsText((next.social_profile_domains || []).join("\n"));
            toast.success("Tracker saved");
        } catch {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="tracker-detail-page" className="max-w-3xl">
            <Link
                to="/vertical-trackers"
                className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.15em] text-[#9ca3af] hover:text-[#f3f4f6] mb-3"
                data-testid="tracker-back"
            >
                <ArrowLeft className="w-3 h-3" /> all trackers
            </Link>

            <div className="flex items-baseline justify-between mb-1">
                <h1 className="text-3xl font-bold tracking-tight">{t.name}</h1>
                <span
                    className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-[2px] border ${
                        t.status === "live"
                            ? "text-[#7cc93c] border-[#7cc93c]/40"
                            : "text-[#9ca3af] border-[#262a2e]"
                    }`}
                    data-testid="tracker-status"
                >
                    {t.status === "live" ? "LIVE" : "COMING LATER"}
                </span>
            </div>
            <div className="font-mono text-[11px] text-[#6b7280] mb-6">slug · {t.slug}</div>

            {t.status === "coming_later" && (
                <div
                    className="mb-6 p-3 border border-[#262a2e] bg-[#0f1113] rounded-[2px] text-[12px] text-[#9ca3af]"
                    data-testid="tracker-coming-later-banner"
                >
                    Not yet wired. You can save defaults here, but campaigns can't link to a
                    coming-later tracker until it's marked live.
                </div>
            )}

            <div className="space-y-4">
                {TEXT_FIELDS.map((f) =>
                    f.input ? (
                        <Field
                            key={f.key}
                            label={f.label}
                            testid={`tracker-${f.key}`}
                            value={t[f.key] ?? ""}
                            onChange={(v) => update(f.key, v)}
                        />
                    ) : (
                        <FieldArea
                            key={f.key}
                            label={f.label}
                            rows={f.rows || 3}
                            testid={`tracker-${f.key}`}
                            value={t[f.key] ?? ""}
                            onChange={(v) => update(f.key, v)}
                        />
                    ),
                )}
                <FieldArea
                    label="Default disqualifiers (one per line)"
                    rows={4}
                    testid="tracker-disqualifiers"
                    value={disqualifiersText}
                    onChange={setDisqualifiersText}
                />
            </div>

            {/* iter23 — per-tracker scoring config */}
            <div className="mt-8 border-t border-[#262a2e] pt-6" data-testid="tracker-scoring-section">
                <h2 className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#9ca3af] mb-1">
                    Scoring thresholds & opportunity points
                </h2>
                <div className="font-mono text-[11px] text-[#6b7280] mb-4">
                    These tune the deterministic Fit/Opportunity scoring for prospects under
                    campaigns linked to this tracker. Empty values fall back to the global
                    default. No scoring outcomes change unless you change a value here.
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {SCORING_NUMBER_FIELDS.map((f) => (
                        <NumberField
                            key={f.key}
                            label={f.label}
                            help={f.help}
                            step={f.step}
                            testid={`tracker-${f.key}`}
                            value={t[f.key]}
                            onChange={(v) => update(f.key, v)}
                        />
                    ))}
                </div>
                <div className="mt-4">
                    <FieldArea
                        label="Social/profile-only domains (one per line, host suffix)"
                        rows={5}
                        testid="tracker-social_profile_domains"
                        value={domainsText}
                        onChange={setDomainsText}
                    />
                    <div className="font-mono text-[11px] text-[#6b7280] mt-1">
                        Suffix-matched against the prospect's website host. Examples:
                        facebook.com, instagram.com, linktr.ee, wixsite.com.
                    </div>
                </div>
            </div>

            <div className="mt-6 flex items-center gap-3">
                <button
                    onClick={save}
                    disabled={saving}
                    data-testid="tracker-save-btn"
                    className="flex items-center gap-2 bg-[#7cc93c] text-[#090a0b] hover:bg-[#9be05c] disabled:opacity-50 font-mono text-[12px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-[2px]"
                >
                    <Save className="w-3.5 h-3.5" />
                    {saving ? "Saving…" : "Save tracker"}
                </button>
                {t.updated_at && (
                    <span className="font-mono text-[11px] text-[#6b7280]">
                        last updated {new Date(t.updated_at).toLocaleString()}
                    </span>
                )}
            </div>
        </div>
    );
}

function Field({ label, value, onChange, testid }) {
    return (
        <div>
            <label className="block font-mono text-[11px] tracking-[0.2em] uppercase text-[#9ca3af] mb-1">
                {label}
            </label>
            <input
                type="text"
                data-testid={testid}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-[#0f1113] border border-[#262a2e] rounded-[2px] px-3 py-2 font-mono text-[12px] text-[#f3f4f6] focus:outline-none focus:border-[#7cc93c]"
            />
        </div>
    );
}

function FieldArea({ label, value, onChange, rows = 3, testid }) {
    return (
        <div>
            <label className="block font-mono text-[11px] tracking-[0.2em] uppercase text-[#9ca3af] mb-1">
                {label}
            </label>
            <textarea
                rows={rows}
                data-testid={testid}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-[#0f1113] border border-[#262a2e] rounded-[2px] px-3 py-2 font-mono text-[12px] text-[#f3f4f6] focus:outline-none focus:border-[#7cc93c]"
            />
        </div>
    );
}

function NumberField({ label, value, onChange, testid, help, step = 1 }) {
    return (
        <div>
            <label className="block font-mono text-[11px] tracking-[0.2em] uppercase text-[#9ca3af] mb-1">
                {label}
            </label>
            <input
                type="number"
                step={step}
                data-testid={testid}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value === "" ? "" : e.target.value)}
                placeholder="default"
                className="w-full bg-[#0f1113] border border-[#262a2e] rounded-[2px] px-3 py-2 font-mono text-[12px] text-[#f3f4f6] focus:outline-none focus:border-[#7cc93c]"
            />
            {help ? (
                <div className="font-mono text-[11px] text-[#6b7280] mt-1">{help}</div>
            ) : null}
        </div>
    );
}
